import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import {
  useTestCase,
  useTestCaseSteps,
  useDeleteTestStep,
} from '../api/useTestCases'
import { TestImportance, ExecutionType } from '@/types/testcase.types'
import type { TestStep } from '@/types/testcase.types'
import { TestStepDialog } from '../components/TestStepDialog'
import { logger } from '@/lib/utils/logger'

export default function TestCaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const testCaseId = parseInt(id || '0', 10)

  // openapi: GET /api/testcases/{testcase_id}
  const { data: testCase, isLoading: isLoadingTC, error: tcError } = useTestCase(testCaseId)
  // openapi: GET /api/testcases/{testcase_id}/steps
  const { data: steps, isLoading: isLoadingSteps, error: stepsError } = useTestCaseSteps(testCaseId)
  const deleteStepMutation = useDeleteTestStep()

  const [stepDialogOpen, setStepDialogOpen] = useState(false)
  const [stepDialogMode, setStepDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedStep, setSelectedStep] = useState<TestStep | undefined>()

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [stepToDelete, setStepToDelete] = useState<{ id: number; stepNumber: number } | null>(null)

  const handleCreateStep = () => {
    setStepDialogMode('create')
    setSelectedStep(undefined)
    setStepDialogOpen(true)
  }

  const handleEditStep = (step: TestStep) => {
    setStepDialogMode('edit')
    setSelectedStep(step)
    setStepDialogOpen(true)
  }

  const handleDeleteStepClick = (stepId: number, stepNumber: number) => {
    setStepToDelete({ id: stepId, stepNumber })
    setDeleteConfirmOpen(true)
  }

  // openapi: DELETE /api/testcases/{testcase_id}/steps/{step_id} → 204
  const handleDeleteStepConfirm = async () => {
    if (!stepToDelete) return
    try {
      await deleteStepMutation.mutateAsync({ testcaseId: testCaseId, stepId: stepToDelete.id })
      toast.success(`Step ${stepToDelete.stepNumber} deleted`)
    } catch (err) {
      logger.error('Delete step failed', { error: err })
      toast.error('Failed to delete step')
    } finally {
      setDeleteConfirmOpen(false)
      setStepToDelete(null)
    }
  }

  const getImportanceLabel = (importance: number) => {
    switch (importance) {
      case TestImportance.Low: return 'Low'
      case TestImportance.Medium: return 'Medium'
      case TestImportance.High: return 'High'
      default: return '—'
    }
  }

  const getImportanceBadgeVariant = (importance: number) => {
    switch (importance) {
      case TestImportance.High: return 'destructive' as const
      case TestImportance.Medium: return 'warning' as const
      default: return 'default' as const
    }
  }

  const isLoading = isLoadingTC || isLoadingSteps
  const error = tcError || stepsError

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load test case'}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/tests/test-cases')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Test Cases
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!testCase) {
    return (
      <div className="flex flex-col gap-6">
        <Alert>
          <AlertDescription>Test case not found.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/tests/test-cases')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Test Cases
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tests/test-cases')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{testCase.name}</h1>
          <p className="text-sm text-muted-foreground">ID: {testCase.id} · Version: {testCase.version}</p>
        </div>
      </div>

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>Test Case Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Importance</p>
              <Badge variant={getImportanceBadgeVariant(testCase.importance)}>
                {getImportanceLabel(testCase.importance)}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Execution Type</p>
              <Badge variant={testCase.execution_type === ExecutionType.Automated ? 'default' : 'outline'}>
                {testCase.execution_type === ExecutionType.Manual ? 'Manual' : 'Automated'}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Author</p>
              <p className="text-sm">{testCase.author_name || `User #${testCase.author_id}`}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Est. Duration</p>
              <p className="text-sm">
                {testCase.estimated_exec_duration ? `${testCase.estimated_exec_duration} min` : '—'}
              </p>
            </div>
          </div>
          {testCase.summary && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground">Summary</p>
              <p className="text-sm mt-1 whitespace-pre-wrap">{testCase.summary}</p>
            </div>
          )}
          {testCase.preconditions && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground">Preconditions</p>
              <p className="text-sm mt-1 whitespace-pre-wrap">{testCase.preconditions}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Steps Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Test Steps ({steps?.length || 0})</CardTitle>
            <Button size="sm" onClick={handleCreateStep}>
              <Plus className="mr-2 h-4 w-4" /> Add Step
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!steps || steps.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No steps defined. Add a step to define the test procedure.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">#</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Expected Results</TableHead>
                    <TableHead className="w-[100px]">Type</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {steps
                    .sort((a, b) => a.step_number - b.step_number)
                    .map((step) => (
                      <TableRow key={step.id}>
                        <TableCell className="font-medium">{step.step_number}</TableCell>
                        <TableCell className="whitespace-pre-wrap">{step.actions}</TableCell>
                        <TableCell className="whitespace-pre-wrap">{step.expected_results}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {step.execution_type === ExecutionType.Automated ? 'Auto' : 'Manual'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditStep(step)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteStepClick(step.id, step.step_number)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step Dialog */}
      <TestStepDialog
        open={stepDialogOpen}
        onOpenChange={setStepDialogOpen}
        testCaseId={testCaseId}
        testStep={selectedStep}
        mode={stepDialogMode}
      />

      {/* Delete Step Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Step"
        description={`Are you sure you want to delete step ${stepToDelete?.stepNumber}? This action cannot be undone.`}
        onConfirm={handleDeleteStepConfirm}
        loading={deleteStepMutation.isPending}
        variant="destructive"
      />
    </div>
  )
}
